import React, { useState, useEffect } from 'react';
import Sidebar from '../components/Layout/Sidebar';
import ClientTable from '../components/Dashboard/ClientTable';
import AddClientModal from '../components/Dashboard/AddClientModal';
import ClientDetail from '../components/Dashboard/ClientDetail';
import { api } from '../api';
import { Users } from 'lucide-react';

export default function Dashboard({ onLogout }) {
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [selectedClientId, setSelectedClientId] = useState(null);

  const loadData = async () => {
    try {
      const data = await api('/api/clients/search');
      setClients(Array.isArray(data) ? data : [data]);
    } catch (err) {
      console.error('Failed to load clients', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const handleClientAdded = (newClient) => {
    setClients(prev => [...prev, newClient]);
    setShowModal(false);
  };

  return (
    <div className="flex h-screen bg-slate-50">
      <Sidebar />
      <div className="flex-1 flex flex-col overflow-hidden">
        <header className="bg-white border-b px-8 py-4 flex justify-between items-center">
          <h1 className="text-xl font-bold text-slate-800">
            {selectedClientId ? 'Client Detail' : 'Client Management'}
          </h1>
          <div className="flex items-center space-x-4">
            <span className="text-sm text-slate-500">Active Agency</span>
            <div className="h-8 w-8 bg-blue-600 rounded-full"></div>
          </div>
        </header>

        <main className="flex-1 overflow-y-auto p-8">
          {selectedClientId ? (
            <ClientDetail
              clientId={selectedClientId}
              onBack={() => setSelectedClientId(null)}
            />
          ) : loading ? (
            <div className="text-center py-10 text-slate-500">Loading your database...</div>
          ) : clients.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full text-center">
              <div className="bg-white rounded-2xl shadow-sm border p-12 max-w-md w-full">
                <div className="flex items-center justify-center w-16 h-16 bg-blue-50 rounded-2xl mx-auto mb-6">
                  <Users size={32} className="text-blue-600" />
                </div>
                <h2 className="text-xl font-bold text-slate-800 mb-2">Add your first client</h2>
                <p className="text-slate-500 text-sm mb-8">
                  Get started by adding your first client to your agency. You can assign producers and account executives to each client.
                </p>
                <button
                  onClick={() => setShowModal(true)}
                  className="w-full bg-blue-600 text-white px-6 py-3 rounded-xl text-sm font-semibold hover:bg-blue-700 transition"
                >
                  + Add First Client
                </button>
              </div>
            </div>
          ) : (
            <>
              <div className="flex justify-end mb-4">
                <button
                  onClick={() => setShowModal(true)}
                  className="bg-blue-600 text-white px-4 py-2 rounded-lg text-sm font-semibold hover:bg-blue-700 transition"
                >
                  + Add Client
                </button>
              </div>
              <ClientTable
                clients={clients}
                onClientClick={(id) => setSelectedClientId(id)}
                onClientDeleted={(id) => setClients(prev => prev.filter(c => c.id !== id))}
              />
            </>
          )}
        </main>
      </div>

      {showModal && (
        <AddClientModal
          onClose={() => setShowModal(false)}
          onClientAdded={handleClientAdded}
        />
      )}
    </div>
  );
}